// Celdas `html:` y filtros compartidos por los listados de las entidades de
// Emergencias Médicas (hospitales, bases de operaciones, personal de salud y
// pacientes). Mismo estilo que los listados de luminarias.
import { IFiltroTabla } from '../../../../auxiliares/tabla/filtro/filtro.component';
import { IDestinatarioAsistencia } from 'modelos/src';
import { HelperService } from '../../../../auxiliares/servicios/helper.service';
import { htmlBadge, IPresentacion } from '../estado-emergencia';
import { IEstadoCelda } from '../../../../auxiliares/tabla/tabla.interfaces';

/** Escapa texto cargado por usuarios antes de meterlo en una celda `html:`. */
export function esc(texto?: string | number | null): string {
  if (texto === undefined || texto === null) return '';
  return String(texto)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function sinDato(texto: string): string {
  return `<span class="no-info">${texto}</span>`;
}

// Activo / inactivo

const ACTIVO: IPresentacion = {
  label: 'Activo',
  color: '#2ECC71',
  icono: 'check_circle',
};
const INACTIVO: IPresentacion = {
  label: 'Inactivo',
  color: '#9e9e9e',
  icono: 'block',
};

export function presentacionActivo(activo?: boolean | null): IPresentacion {
  return activo ? ACTIVO : INACTIVO;
}

export function htmlActivo(activo?: boolean | null): string {
  return htmlBadge(presentacionActivo(activo));
}

/** Activo / Inactivo para la pieza `estado` de los listados. */
export function estadoActivo(activo?: boolean | null): IEstadoCelda {
  return activo
    ? { texto: 'Activo', tono: 'ok', icono: 'toggle_on' }
    : { texto: 'Inactivo', tono: 'neutro', icono: 'toggle_off' };
}

/**
 * Filtro Activo / Inactivo. "Inactivo" incluye los documentos sin el campo
 * (`$ne: true`), que en los datos viejos son muchos.
 */
export function filtroActivo(campo: string): IFiltroTabla {
  return {
    elementos: [
      {
        label: ACTIVO.label,
        icon: 'toggle_on',
        iconColor: 'var(--color-green-text)',
        value: [{ [campo]: true }],
      },
      {
        label: INACTIVO.label,
        icon: 'toggle_off',
        iconColor: 'var(--color-gray-text)',
        value: [{ [campo]: { $ne: true } }],
      },
    ],
    filter: {
      field: '$or',
    },
    label: 'Estado',
    rapido: true,
    tipo: 'select',
    selectIcon: true,
    selectLabel: 'label',
    selectValue: 'value',
    compararPorJson: true,
  };
}

/** Ícono del personal de salud según el rol. */
export function iconoRolSalud(rol?: string | null): string {
  return rol === 'Enfermero' ? 'vaccines' : 'medical_services';
}

// Pacientes

export const SEXOS: { label: string; value: 'M' | 'F' | 'X' }[] = [
  { label: 'Masculino', value: 'M' },
  { label: 'Femenino', value: 'F' },
  { label: 'X', value: 'X' },
];

export function labelSexo(sexo?: string | null): string | null {
  return SEXOS.find((s) => s.value === sexo)?.label ?? null;
}

export function nombrePaciente(p?: IDestinatarioAsistencia | null): string {
  return [p?.nombre, p?.apellido].filter(Boolean).join(' ').trim();
}

/** "DNI 30.123.456 · 45 años · Femenino" (ya escapado). */
export function resumenPaciente(p?: IDestinatarioAsistencia | null): string {
  const partes: string[] = [];
  if (p?.dni) partes.push(`DNI ${esc(p.dni)}`);
  if (p?.edad !== undefined && p?.edad !== null)
    partes.push(`${p.edad} ${p.edad === 1 ? 'año' : 'años'}`);
  const sexo = labelSexo(p?.sexo);
  if (sexo) partes.push(sexo);
  return partes.join(' · ');
}

// Celdas (clases de app-tabla, docs/GUIA_ESTETICA_LISTADOS.md)

/** Ícono de la entidad + nombre (línea principal) + líneas secundarias. */
export function htmlEntidad(
  icono: string,
  principal: string | undefined | null,
  secundarias: (string | undefined | null)[] = [],
  sinNombre = 'Sin nombre',
): string {
  let html = `<div class="tabla-celda-fila">`;
  html += `<span class="material-symbols-outlined tabla-icono-dato">${icono}</span>`;
  html += `<div class="tabla-celda-col">`;
  html += principal
    ? `<span class="tabla-principal">${esc(principal)}</span>`
    : sinDato(sinNombre);
  for (const s of secundarias) {
    if (s) html += `<span class="tabla-secundario">${s}</span>`;
  }
  html += `</div></div>`;
  return html;
}

/**
 * Teléfono(s) y email, uno por línea. Sin íconos: teléfono y email no están
 * en docs/ICONOS.md; el alternativo lleva su rótulo para no confundirlo.
 */
export function htmlContacto(
  telefono?: string | null,
  email?: string | null,
  telefonoAlternativo?: string | null,
): string {
  const lineas: string[] = [];
  if (telefono) lineas.push(`<span>${esc(telefono)}</span>`);
  if (telefonoAlternativo)
    lineas.push(
      `<span class="tabla-secundario">Alternativo: ${esc(telefonoAlternativo)}</span>`,
    );
  if (email) lineas.push(`<span class="tabla-secundario">${esc(email)}</span>`);
  if (!lineas.length) return sinDato('Sin contacto');
  return `<div class="tabla-celda-col">${lineas.join('')}</div>`;
}

/**
 * Dirección corta para la celda (como luminarias: sólo calle y número). La
 * completa va en el tooltip: `tooltipDireccion`.
 */
export function htmlDireccion(direccion?: string | null): string {
  if (!direccion) return sinDato('Sin dirección');
  const corta = HelperService.extraerDireccion(direccion) || direccion;
  return `<div class="tabla-linea"><span class="material-symbols-outlined">place</span><span>${esc(corta)}</span></div>`;
}

/** Tooltip con la dirección completa (undefined si no hay). */
export function tooltipDireccion(
  direccion?: string | null,
): string | undefined {
  return direccion
    ? `<div>Dirección: <strong>${esc(direccion)}</strong></div>`
    : undefined;
}

/**
 * Valor para `app-info-table`: `null` se muestra como "Sin información". No se
 * escapa: info-table muestra todo string como texto (solo pinta como HTML lo
 * marcado con htmlInfoTable), y escapado se vería "A &amp; B".
 */
export function valorInfo(valor?: string | number | null): string | null {
  if (valor === undefined || valor === null || valor === '') return null;
  return String(valor);
}
